// Single source for the keyboard cheat sheet (the `?` dialog and
// Settings → Keyboard shortcuts) and the key hints in the Ctrl+K command
// palette. Each alternative is a key sequence — ["g", "i"] reads "g then i".
// Keep in step with useMailShortcuts.ts.

export interface ShortcutItem {
    /** Stable id so other surfaces (the command palette) can show the same keys. */
    id?: string;
    alternatives: string[][];
    description: string;
}

export interface ShortcutGroup {
    section: string;
    items: ShortcutItem[];
}

export const SHORTCUT_GROUPS: ShortcutGroup[] = [
    {
        section: "Navigation",
        items: [
            { alternatives: [["j"], ["↓"]], description: "Next message / conversation" },
            { alternatives: [["k"], ["↑"]], description: "Previous message / conversation" },
            { alternatives: [["o"], ["Enter"]], description: "Open — moves focus into the reading pane" },
            { alternatives: [["u"]], description: "Back to the list" },
            { id: "search", alternatives: [["/"]], description: "Search mail" },
            { id: "goto-inbox", alternatives: [["g", "i"]], description: "Go to Inbox" },
            { id: "goto-sent", alternatives: [["g", "s"]], description: "Go to Sent" },
            { id: "goto-drafts", alternatives: [["g", "d"]], description: "Go to Drafts" },
            { id: "goto-trash", alternatives: [["g", "t"]], description: "Go to Trash" },
            { id: "palette", alternatives: [["Ctrl+K"]], description: "Command palette" }
        ]
    },
    {
        section: "Message actions",
        items: [
            { alternatives: [["e"]], description: "Archive" },
            { alternatives: [["#"], ["Delete"]], description: "Delete (move to Trash)" },
            { alternatives: [["s"]], description: "Star / flag" },
            { alternatives: [["Shift+I"]], description: "Mark as read" },
            { alternatives: [["Shift+U"]], description: "Mark as unread" },
            { alternatives: [["x"]], description: "Select" },
            { alternatives: [["z"]], description: "Undo the last action" }
        ]
    },
    {
        section: "Compose",
        items: [
            { id: "compose", alternatives: [["c"], ["n"], ["Shift+C"]], description: "New message" },
            { alternatives: [["r"]], description: "Reply" },
            { alternatives: [["a"]], description: "Reply all" },
            { alternatives: [["f"]], description: "Forward" },
            { alternatives: [["Ctrl+Enter"]], description: "Send (in compose)" }
        ]
    },
    {
        section: "Help",
        items: [{ id: "help", alternatives: [["?"]], description: "Show keyboard shortcuts" }]
    }
];

/** The first key sequence of a catalogued shortcut, e.g. ["g", "i"]. */
export function shortcutKeys(id: string): string[] | undefined {
    for (const group of SHORTCUT_GROUPS) {
        const item = group.items.find((i) => i.id === id);
        if (item) return item.alternatives[0];
    }
    return undefined;
}
